import axios from 'axios';
import { fetchToken } from '../fetchToken';
import { logError } from '../../taskpane/utils/request';

const API_URL = process.env.EMAILS_API_URL;
axios.defaults.withCredentials = true;

/**
 * Get Files that were saved to Docrio
 * @param {string} recordId - id of record to get files from
 * @param {Object} nextBatch - object containing data to retreive next batch of files
 * @return {Object} - data response from Docrio
 */
export const getFiles = async (recordId, nextBatch) => {
  const token = await fetchToken();
  const config = {
    method: 'GET',
    url: `${API_URL}docrio/files?recordId=${recordId}${nextBatch ? `&OffsetId=${nextBatch.OffsetId}&OffsetValue=${nextBatch.OffsetValue}` : ''}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return (await axios(config)).data;
  } catch (e) {
    logError(e);
    console.log('Failed to get files');
    return false;
  }
};

/**
 * Get links for files in Docrio
 * @param {Array} fileIds - array of file ids
 * @return {Object} - data response from Docrio
 */
export const getFileLinks = async (fileIds) => {
  const token = await fetchToken();
  const config = {
    method: 'GET',
    url: `${API_URL}docrio/files/download?fileIds=${fileIds.toString()}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return (await axios(config)).data;
  } catch (e) {
    logError(e);
    console.log('Failed to get file links', e);
    throw e;
  }
};

/**
 * Posts files to the server
 * @param {string} id - email id
 * @param {object} body - files
 * @param {string} ownerEmail - email address of the owner
 * @return {Promise} - attachments and emlHash
 */
export const postFilesComplete = async (id, body, ownerEmail) => {
  const token = await fetchToken();
  let url = API_URL + 'emails/' + id + '/attachments/complete';
  if (ownerEmail) {
    url += `?ownerEmail=${encodeURIComponent(ownerEmail)}`;
  }
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  return axios.post(url, body, { headers });
};

/**
 * Runs a pre-upload dry-run validation of candidate File records against Salesforce save-time
 * rules (validation rules + trigger addError). No records are persisted. Fails closed: on any
 * error the promise rejects so the caller aborts the upload.
 * @param {Array<object>} files - candidate files with neutral keys: { name, fileType, fileSize,
 *   folderPath, relatedToId, relatedToApiName, portalAccountId }
 * @param {string} ownerEmail - email address of the owner
 * @return {Promise<Array<{index: number, fileName: string, messages: string[]}>>} violations
 */
export const validateFiles = async (files, ownerEmail) => {
  const token = await fetchToken();
  let url = `${API_URL}docrio/files/validate`;
  if (ownerEmail) {
    url += `?ownerEmail=${encodeURIComponent(ownerEmail)}`;
  }
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const { data } = await axios.post(url, { files }, { headers });
  return data?.violations || [];
};

/**
 * Get Folders from a record in Docrio
 * @param {string} recordId - Id of a location in Docrio
 * @return {Promise} - Folders in the record
 */
export const getFolders = async (recordId) => {
  const token = await fetchToken();
  const config = {
    method: 'GET',
    url: `${API_URL}docrio/folders?recordId=${recordId}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    return (await axios(config)).data;
  } catch (e) {
    logError(e);
    console.log('Failed to get folders');
    return false;
  }
};
